export const articleTemplateHtml = `
  <h2>这里填写文章主标题</h2>
  <p>这里填写正文开头摘要，用来说明这篇技术手记想解决什么问题。</p>
  <figure class="article-figure">
    <img src="/images/portal-info.webp" alt="文章配图">
    <figcaption>这里填写图片说明。</figcaption>
  </figure>
  <section class="article-code">
    <div class="article-code-head">
      <span>example.js</span>
      <button type="button" data-copy-code="console.log('Hello, world!')">复制代码</button>
    </div>
    <pre><code>console.log("Hello, world!")</code></pre>
  </section>
`;

export const defaultTechNotes = [
  {
    slug: "tech-handbook",
    title: "技术手记",
    summary: "收录我的实践经验，把复杂过程整理成可读可复用的教程。",
    heroSummary: "这篇文章示范技术手记详情页的完整结构，包含图片、视频、代码块和提示框等内容模块。",
    image: "/images/portal-info.webp",
    date: "2026-04-04",
    reading: "阅读时间 6 分钟",
    categorySlug: "frontend",
    categoryTitle: "前端排版",
    codeBlocks: 2,
    contentHtml: `
      <h2>为什么要把经验整理成手记</h2>
      <p>当一个页面从最初的视觉想法，逐步变成可以真正上线的作品时，中间往往要经历大量细碎但关键的决策。把这些过程记录下来，不只是为了以后复盘，也是为了让下一次的创作更快进入状态。</p>
      <div class="article-callout is-info">
        <strong>写作建议</strong>
        <p>一篇好的技术手记，不一定需要复杂术语，但一定要让读者知道“为什么这样做”和“做完会得到什么”。</p>
      </div>
      <figure class="article-figure">
        <img src="/images/portal-info.webp" alt="技术手记封面示例">
        <figcaption>用一张视觉图先建立文章的气质和阅读预期。</figcaption>
      </figure>
      <h3>适合技术手记的正文结构</h3>
      <p>比较稳定的结构通常是：问题背景、页面目标、关键拆解、实现方式、踩坑记录、最终结果。这样读者既能看到结果，也能理解决策过程。</p>
      <section class="article-code">
        <div class="article-code-head">
          <span>article-layout.css</span>
          <button type="button" data-copy-code=".article-card { display: grid; grid-template-columns: 320px 1fr; gap: 24px; }">复制代码</button>
        </div>
        <pre><code>.article-card {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 24px;
}</code></pre>
      </section>
      <h3>视频也可以作为讲解的一部分</h3>
      <p>当某个动画、交互或滚动细节不容易用静态图说明时，插入一段演示视频会更直观。</p>
      <section class="article-video">
        <div class="article-video-frame">
          <iframe
            class="article-video-embed"
            src="https://www.youtube.com/embed/dQw4w9WgXcQ"
            title="技术手记视频示例"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>
        <p class="article-video-note">这里是视频模块的示意位，后面你可以替换成自己的讲解视频或录屏。</p>
      </section>
      <section class="article-code">
        <div class="article-code-head">
          <span>notes.js</span>
          <button type="button" data-copy-code="const article = notes.find((item) => item.slug === currentSlug)">复制代码</button>
        </div>
        <pre><code>const article = notes.find((item) =&gt; item.slug === currentSlug)

if (!article) {
  renderEmptyState()
}</code></pre>
      </section>
    `,
  },
  {
    slug: "vision-domain",
    title: "光影音域",
    summary: "记录我的视频作品与视觉表达，把一闪而过的灵感打磨成可以被看见的内容。",
    heroSummary: "这一页展示视觉类内容的文章排版方式，适合放项目过程、镜头拆解和风格笔记。",
    image: "/images/portal-dev.webp",
    date: "2026-04-03",
    reading: "阅读时间 5 分钟",
    categorySlug: "ai-creation",
    categoryTitle: "AI 工作流",
    codeBlocks: 1,
    contentHtml: `
      <h2>从画面气质开始定义一篇视觉文章</h2>
      <p>当文章主要讨论镜头语言、节奏、构图与视觉氛围时，页面就不应该只是普通博客式排版，而要让图片、视频和留白承担更多叙事作用。</p>
      <figure class="article-figure">
        <img src="/images/portal-dev.webp" alt="光影音域封面示例">
        <figcaption>图片、视频和正文之间的节奏关系，会决定整篇文章的阅读体验。</figcaption>
      </figure>
      <div class="article-callout is-tip">
        <strong>视觉类文章建议</strong>
        <p>一段正文不要过长，每一屏最好都有明确的视觉焦点，否则读者会很快失去注意力。</p>
      </div>
      <section class="article-code">
        <div class="article-code-head">
          <span>color-palette.json</span>
          <button type="button" data-copy-code='{
  "primary": "#4f7cff",
  "glow": "rgba(79, 124, 255, 0.24)"
}'>复制代码</button>
        </div>
        <pre><code>{
  "primary": "#4f7cff",
  "glow": "rgba(79, 124, 255, 0.24)"
}</code></pre>
      </section>
    `,
  },
  {
    slug: "audio-blank",
    title: "声音留白",
    summary: "这里存放旋律，也存放情绪，是我与声音世界之间的一种安静对话。",
    heroSummary: "适合记录音频创作、配乐笔记和声音实验的内容页面结构。",
    image: "/images/portal-visual.webp",
    date: "2026-04-02",
    reading: "阅读时间 4 分钟",
    categorySlug: "deployment",
    categoryTitle: "部署记录",
    codeBlocks: 1,
    contentHtml: `
      <h2>声音也需要被好好书写</h2>
      <p>当你在网页里记录音乐、采样、配音或情绪片段时，排版节奏应该更柔和一点，让读者在阅读时有“停顿”和“回味”的空间。</p>
      <section class="article-video">
        <div class="article-video-frame">
          <video class="article-video-player" controls preload="metadata" src="https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4"></video>
        </div>
        <p class="article-video-note">这里示意本地或远程 MP4 视频/音频讲解内容的嵌入方式。</p>
      </section>
      <div class="article-callout is-warning">
        <strong>注意</strong>
        <p>如果文章里有可试听内容，最好同时附一段文字说明，帮助读者理解这一段声音想表达什么。</p>
      </div>
      <section class="article-code">
        <div class="article-code-head">
          <span>audio-note.md</span>
          <button type="button" data-copy-code="BPM: 92&#10;Mood: calm / airy / distant">复制代码</button>
        </div>
        <pre><code>BPM: 92
Mood: calm / airy / distant</code></pre>
      </section>
    `,
  },
  {
    slug: "portal-layout",
    title: "把一个普通门户页做成有节奏感的内容首页",
    summary: "从模块比例、交互反馈到 hover 细节，梳理视觉层次该如何一步步建立。",
    heroSummary: "一篇关于首页门户卡片、导航和视觉节奏的排版笔记。",
    image: "/images/portal-dev.webp",
    date: "2026-04-01",
    reading: "阅读时间 6 分钟",
    categorySlug: "frontend",
    categoryTitle: "前端排版",
    codeBlocks: 1,
    contentHtml: `
      <h2>模块比例会直接影响首页气质</h2>
      <p>同样是三张卡片，图片区域更大还是文字区域更大，会直接改变页面的气质。越偏作品展示型，图片权重越要足够。</p>
      <section class="article-code">
        <div class="article-code-head">
          <span>portal-card.css</span>
          <button type="button" data-copy-code=".portal-card__media { min-height: 198px; }">复制代码</button>
        </div>
        <pre><code>.portal-card__media {
  min-height: 198px;
}</code></pre>
      </section>
    `,
  },
  {
    slug: "server-deploy-note",
    title: "腾讯云轻量服务器 + 1Panel：个人站点静态部署流程备忘",
    summary: "记录从本地开发、文件同步到 Nginx / 1Panel 托管的完整过程，方便以后快速复用。",
    heroSummary: "这是一篇偏部署记录的手记，适合整理上线流程和环境配置。",
    image: "/images/portal-info.webp",
    date: "2026-03-29",
    reading: "阅读时间 7 分钟",
    categorySlug: "deployment",
    categoryTitle: "部署记录",
    codeBlocks: 2,
    contentHtml: `
      <h2>部署文章最好记录到命令粒度</h2>
      <p>比起抽象说明，真正有价值的是“命令、目录、配置位置、验证方式”都清清楚楚写出来。</p>
      <section class="article-code">
        <div class="article-code-head">
          <span>deploy.sh</span>
          <button type="button" data-copy-code="scp -r ./dist ubuntu@server:/var/www/site">复制代码</button>
        </div>
        <pre><code>scp -r ./dist ubuntu@server:/var/www/site</code></pre>
      </section>
      <section class="article-code">
        <div class="article-code-head">
          <span>nginx.conf</span>
          <button type="button" data-copy-code="root /var/www/site;&#10;index index.html;">复制代码</button>
        </div>
        <pre><code>root /var/www/site;
index index.html;</code></pre>
      </section>
    `,
  },
  {
    slug: "workflow-notes",
    title: "把杂乱操作整理成可重复流程：从临时经验到稳定工作流",
    summary: "把经常会重复做的事情固化成流程模板，减少重试成本，也让协作更轻松。",
    heroSummary: "这篇文章适合放流程拆解、工具组合和标准化模板。",
    image: "/images/portal-visual.webp",
    date: "2026-03-27",
    reading: "阅读时间 5 分钟",
    categorySlug: "efficiency-tools",
    categoryTitle: "效率工具",
    codeBlocks: 1,
    contentHtml: `
      <h2>稳定工作流的价值</h2>
      <p>流程被整理以后，人的注意力才能更多地留给判断和创造，而不是每次都从头试一遍。</p>
      <div class="article-callout is-info">
        <strong>核心原则</strong>
        <p>把会重复出现的步骤写成清单，再把清单固化成模板。</p>
      </div>
    `,
  },
];

export const techNotes = defaultTechNotes;
